import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'framer-motion'

// Exiting pages remain visible briefly, but must not retain interactive controls.
function TransitionPage({ children }: { children: ReactNode }) {
  const reduceMotion = useReducedMotion()
  const isPresent = useIsPresent()
  const pageRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!isPresent) return
    const focused = document.activeElement
    if (focused === document.body || focused?.closest('[inert]')) {
      pageRef.current?.focus({ preventScroll: true })
    }
  }, [isPresent])
  return (
    <motion.div
      initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{
        opacity: 0,
        y: reduceMotion ? 0 : -3,
        transition: { duration: reduceMotion ? 0 : 0.14 },
      }}
      transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="col-start-1 row-start-1 h-full min-w-0 outline-none"
      ref={pageRef}
      tabIndex={-1}
      // inert already removes exiting content from focus and the accessibility tree.
      // Applying aria-hidden first can hide the link which initiated navigation.
      {...(!isPresent ? { inert: '' } : {})}
      style={{ pointerEvents: isPresent ? undefined : 'none' }}
    >
      {children}
    </motion.div>
  )
}

export function PageTransition({
  routeKey,
  children,
  onExitComplete,
}: {
  routeKey: string
  children: ReactNode
  onExitComplete?: () => void
}) {
  return (
    <div className="grid h-full min-w-0">
      <AnimatePresence initial={false} mode="sync" onExitComplete={onExitComplete}>
        {children && <TransitionPage key={routeKey}>{children}</TransitionPage>}
      </AnimatePresence>
    </div>
  )
}
